import {
  ComponentProps,
  MouseEvent,
  useTransition,
  type ReactNode,
} from "react";
import { useFormStatus } from "react-dom";

type TextProps = { children: ReactNode; icon?: never; label?: never };
type IconProps = { label: string; icon: ReactNode; children?: never };

type Props = {
  disabled?: boolean;
  loading?: boolean;
  action?: (event: MouseEvent<HTMLButtonElement>) => Promise<void> | void;
  size?: "sm";
} & (TextProps | IconProps);

type ButtonProps = Omit<ComponentProps<"button">, "children"> & Props;

export const Button = ({
  loading,
  disabled,
  icon,
  label,
  theme = icon ? "text" : "primary",
  size,
  children,
  type,
  action,
  onClick,
  ...rest
}: ButtonProps) => {
  const [transitioning, startTransition] = useTransition();
  const { pending } = useFormStatus();
  const isLoading = loading ?? (pending || transitioning);
  const isInactive = isLoading || disabled;

  return (
    <button
      {...rest}
      theme={theme}
      data-size={size}
      data-loading={isLoading || undefined}
      aria-disabled={isInactive}
      type={action || onClick ? "button" : type}
      onClick={(event) => {
        // Disabled/loading button cannot be clicked
        if (isInactive) event.preventDefault();
        // If button with action run in a transition for pending state
        else if (action) startTransition(() => action(event));
        // Otherwise normal onClick
        else onClick?.(event);
      }}
    >
      {icon || children}
      {label && <span className="Tooltip">{label}</span>}
    </button>
  );
};
